import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarCheck, AlertTriangle, CheckCircle2, CircleSlash, Clock, ShieldQuestion, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATE_META = {
  complete: { label: "Complete", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
  incomplete: { label: "Incomplete", cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: AlertTriangle },
  exception: { label: "Exception", cls: "text-red-700 bg-red-50 border-red-200", Icon: AlertTriangle },
  not_expected: { label: "Not expected — site closed", cls: "text-slate-500 bg-slate-50 border-slate-200", Icon: CircleSlash },
  no_applicable_records: { label: "No records", cls: "text-slate-500 bg-slate-50 border-slate-200", Icon: CircleSlash },
  module_disabled: { label: "Module off", cls: "text-slate-400 bg-slate-50 border-slate-200", Icon: CircleSlash },
  outstanding: { label: "Action outstanding", cls: "text-red-700 bg-red-50 border-red-200", Icon: AlertTriangle },
  recorded: { label: "Action recorded", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
};

const APPROVAL_META = {
  not_reviewed: { label: "Not reviewed", cls: "text-slate-600 bg-slate-100" },
  review_required: { label: "Review required", cls: "text-amber-700 bg-amber-100" },
  approved: { label: "Approved", cls: "text-emerald-700 bg-emerald-100" },
  re_review_required: { label: "Re-review required", cls: "text-red-700 bg-red-100" },
};

const AREA_LABELS = { temperatures: "Temperatures", cleaning: "Cleaning", checklists: "Checklists", prep: "Prep", deliveries: "Deliveries", corrective_actions: "Corrective Actions" };

export default function DailyApproval() {
  const { has } = useAuth();
  const [venues, setVenues] = useState([]);
  const [site, setSite] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ack, setAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/venues").then((r) => { setVenues(r.data); if (r.data[0]) setSite(r.data[0].venue_id); }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!site || !date) return;
    setLoading(true); setAck(false);
    api.get(`/daily-approval/${site}?date=${date}`)
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); toast.error(e?.normalized?.message || "Could not load the daily review"); })
      .finally(() => setLoading(false));
  }, [site, date]);

  useEffect(() => { load(); }, [load]);

  const canApprove = has("approve_daily_approval");
  const outstanding = data?.outstanding_items || [];
  const needsAck = outstanding.length > 0 || (data?.counts?.exceptions || 0) > 0;

  const approve = async () => {
    if (!data) return;
    setSubmitting(true);
    try {
      await api.post(`/daily-approval/${site}/approve`, {
        business_date: date, fingerprint: data.fingerprint,
        ...(needsAck ? { acknowledge_outstanding: true } : {}),
      });
      toast.success("Daily review approved");
      load();
    } catch (e) {
      if (e?.normalized?.status === 409) toast.error("The evidence changed — reloading current data");
      else toast.error(e?.normalized?.message || "Could not approve");
      load();
    } finally { setSubmitting(false); }
  };

  const am = APPROVAL_META[data?.approval_status] || APPROVAL_META.not_reviewed;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="daily-approval-page">
      <div className="flex items-center gap-2">
        <CalendarCheck className="text-brand" size={22} />
        <h1 className="font-display text-2xl font-bold text-slate-800">Daily Compliance Approval</h1>
      </div>
      <p className="text-sm text-slate-500">Review a site's operational evidence for a business date and sign off that you reviewed it. Approval records a review — it never completes or resolves outstanding work.</p>

      <div className="cc-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600">Site</label>
          <select value={site} onChange={(e) => setSite(e.target.value)} data-testid="daily-approval-site-select"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
            {venues.map((v) => <option key={v.venue_id} value={v.venue_id}>{v.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Business date (Europe/London)</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-approval-date-input"
            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>

      {loading ? (
        <div className="cc-card p-10 flex justify-center" data-testid="daily-approval-loading"><Loader2 className="animate-spin text-brand" /></div>
      ) : !data ? (
        <div className="cc-card p-8 text-center text-slate-400" data-testid="daily-approval-empty">No review available.</div>
      ) : (
        <>
          {/* Review status vs evidence status kept explicitly separate */}
          <div className="cc-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="daily-approval-status">
            <div className="flex items-center gap-3">
              <Clock size={18} className="text-slate-400" />
              <div>
                <div className="text-xs text-slate-500">Operating status</div>
                <div className="text-sm font-semibold text-slate-700" data-testid="daily-approval-operating">
                  {data.operating.reporting_required ? "Open — compliance expected" : "Closed — not expected"}
                  <span className="text-slate-400 font-normal"> ({data.operating.status})</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Review status</div>
              <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-bold ${am.cls}`} data-testid="daily-approval-review-status">{am.label}</span>
            </div>
          </div>

          {needsAck && (
            <div className="cc-card p-4 border-l-4 border-amber-400 bg-amber-50/40" data-testid="daily-approval-evidence-warning">
              <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><AlertTriangle size={16} /> Evidence status: outstanding / exception items present</div>
              <p className="text-xs text-amber-700 mt-1">Approving reviews this evidence. It does not complete tasks, resolve exceptions or clear corrective actions.</p>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(data.areas).map(([key, a]) => {
              const meta = STATE_META[a.state] || STATE_META.no_applicable_records;
              return (
                <div key={key} className={`rounded-xl border p-3 ${meta.cls}`} data-testid={`daily-approval-area-${key}`}>
                  <div className="text-xs font-semibold uppercase tracking-wide opacity-70">{AREA_LABELS[key] || key}</div>
                  <div className="flex items-center gap-1.5 mt-1 text-sm font-bold"><meta.Icon size={15} /> {meta.label}</div>
                  {a.enabled && a.expected !== undefined && (
                    <div className="text-xs mt-1 opacity-80">{a.completed}/{a.expected} done{a.incomplete ? ` · ${a.incomplete} left` : ""}</div>
                  )}
                  {a.enabled && a.exceptions ? <div className="text-xs mt-1 font-semibold">{a.exceptions} exception(s)</div> : null}
                </div>
              );
            })}
          </div>

          {outstanding.filter((o) => !o.acknowledged).length > 0 && (
            <div className="cc-card p-4" data-testid="daily-approval-outstanding">
              <h3 className="text-sm font-bold text-slate-700 mb-2">Outstanding & exception items ({outstanding.filter((o) => !o.acknowledged).length})</h3>
              <ul className="space-y-1.5">
                {outstanding.filter((o) => !o.acknowledged).map((o, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600" data-testid={`daily-approval-outstanding-${i}`}>
                    <AlertTriangle size={14} className={o.kind === "exception" ? "text-red-500 mt-0.5" : "text-amber-500 mt-0.5"} />
                    {o.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {outstanding.some((o) => o.acknowledged) && (
            <div className="cc-card p-4 bg-slate-50" data-testid="daily-approval-acknowledged">
              <h3 className="text-sm font-bold text-slate-600 mb-1">Acknowledged by manager — still not completed ({outstanding.filter((o) => o.acknowledged).length})</h3>
              <p className="text-xs text-slate-500 mb-2">These remain recorded as outstanding/exception in the evidence and reports.</p>
              <ul className="space-y-1.5">
                {outstanding.filter((o) => o.acknowledged).map((o, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-500" data-testid={`daily-approval-acknowledged-${i}`}>
                    <AlertTriangle size={14} className="text-slate-400 mt-0.5" />{o.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.approval && (
            <div className="cc-card p-4 text-sm" data-testid="daily-approval-current">
              <span className="text-slate-500">Last approved by </span>
              <span className="font-semibold text-slate-700">{data.approval.reviewer_name}</span>
              <span className="text-slate-500"> on {new Date(data.approval.approved_at).toLocaleString("en-GB")}. </span>
              {data.approval.current ? <span className="text-emerald-600 font-semibold">Current.</span>
                : <span className="text-red-600 font-semibold">Superseded — evidence changed since approval.</span>}
            </div>
          )}

          {canApprove ? (
            <div className="cc-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              {needsAck ? (
                <label className="flex items-start gap-2 text-sm text-slate-700" data-testid="daily-approval-ack-label">
                  <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="daily-approval-ack-checkbox" className="mt-1" />
                  I acknowledge the outstanding / exception items above remain unresolved and I am approving the review of this evidence only.
                </label>
              ) : <span className="text-sm text-slate-500">This day's evidence is complete. Approve to record your review.</span>}
              <button onClick={approve} disabled={submitting || (needsAck && !ack)} data-testid="daily-approval-approve-button"
                className="shrink-0 px-5 py-2.5 rounded-xl bg-brand text-white font-semibold text-sm disabled:opacity-40 hover:opacity-90 transition-opacity">
                {submitting ? "Saving…" : data.approval_status === "approved" ? "Re-approve" : "Approve daily review"}
              </button>
            </div>
          ) : (
            <div className="cc-card p-4 text-sm text-slate-400" data-testid="daily-approval-no-approve-perm">You can view this review but do not have permission to sign it off.</div>
          )}

          {data.history?.length > 0 && (
            <div className="cc-card p-4" data-testid="daily-approval-history">
              <h3 className="text-sm font-bold text-slate-700 mb-2">Approval history</h3>
              <ul className="space-y-1.5 text-xs text-slate-500">
                {data.history.map((h) => (
                  <li key={h.approval_id} className="flex items-center justify-between">
                    <span>{h.event === "reapproved" ? "Re-approved" : "Approved"} by {h.reviewer_name}</span>
                    <span>{new Date(h.created_at).toLocaleString("en-GB")}{h.current ? " · current" : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-slate-400 flex items-start gap-1.5"><ShieldQuestion size={13} className="mt-0.5 shrink-0" /> {data.disclaimer}</p>
        </>
      )}
    </div>
  );
}
